import { useCallback, useEffect, useRef, useState } from "react";

import { Application } from "@core/Application";
import type { QontactPointInformation } from "@models/QontactPoint";
import type { PresenteeismUserRecordSummary } from "@services/QontactPointsService";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import { downloadQontactPointFichajesPdf } from "@components/QontactPoints/QontactPointFichajesPdf";

const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export interface FichajesSummary {
    orgName: string;
    total: number;
    onTime: number;
    late: number;
    withoutShift: number;
    records: PresenteeismUserRecordSummary[];
    totalHoursWorked: string;
    totalExtraHoursWorked: string;
}

const emptySummary: FichajesSummary = {
    orgName: "",
    total: 0,
    onTime: 0,
    late: 0,
    withoutShift: 0,
    records: [],
    totalHoursWorked: "N/A",
    totalExtraHoursWorked: "N/A",
};

const toYearMonth = (year: number, month: number) => `${year}-${month.toString().padStart(2, "0")}`;

/**
 * Fichajes tab of a qontact point: month selector, totals of the month (from
 * presenteeism-summary by building) and the two exports.
 */
export const useQontactPointFichajesTabViewModel = (orgID: string, qontactPoint: QontactPointInformation) => {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    // 1-12
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [summary, setSummary] = useState<FichajesSummary>(emptySummary);
    const [loading, setLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    // Ignore answers of a month the user already left
    const requestRef = useRef(0);

    const yearMonth = toYearMonth(year, month);
    const monthLabel = `${monthNames[month - 1].localized()} ${year}`;
    const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;

    const fetchSummary = useCallback(async (): Promise<FichajesSummary> => {
        const service = Application.shared().coreDependencies.qontactPointsService;
        const result = await service.getPresenteeismResumen(orgID, qontactPoint.buildingID, yearMonth);
        return {
            orgName: result?.orgName ?? "",
            total: result?.total ?? 0,
            onTime: result?.onTime ?? 0,
            late: result?.late ?? 0,
            withoutShift: result?.withoutShift ?? 0,
            records: [...(result?.records ?? [])].sort((a, b) => a.timestamp - b.timestamp),
            totalHoursWorked: result?.totalHoursWorked ?? "N/A",
            totalExtraHoursWorked: result?.totalExtraHoursWorked ?? "N/A",
        };
    }, [orgID, qontactPoint.buildingID, yearMonth]);

    useEffect(() => {
        const request = ++requestRef.current;
        setLoading(true);
        fetchSummary()
            .then((result) => {
                if (request === requestRef.current) {
                    setSummary(result);
                }
            })
            .catch((error) => {
                console.error(error);
                if (request === requestRef.current) {
                    setSummary(emptySummary);
                    ToastManager.shared().showErrorMessage("No se pudieron cargar los fichajes".localized());
                }
            })
            .finally(() => {
                if (request === requestRef.current) {
                    setLoading(false);
                }
            });
    }, [fetchSummary]);

    const previousMonth = () => {
        if (month === 1) {
            setYear((current) => current - 1);
            setMonth(12);
        } else {
            setMonth((current) => current - 1);
        }
    };

    const nextMonth = () => {
        if (isCurrentMonth) {
            return;
        }
        if (month === 12) {
            setYear((current) => current + 1);
            setMonth(1);
        } else {
            setMonth((current) => current + 1);
        }
    };

    const exportData = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            await downloadQontactPointFichajesPdf({
                orgName: summary.orgName,
                qontactPointName: qontactPoint.name,
                yearMonth,
                records: summary.records,
                totalHoursWorked: summary.totalHoursWorked,
                totalExtraHoursWorked: summary.totalExtraHoursWorked,
            });
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage("No se pudo generar el PDF. Comunicate con soporte.".localized());
        } finally {
            setIsExporting(false);
        }
    };

    const exportMonthlyAttendanceSheet = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            // Fresh data, as before: the sheet is usually printed and signed
            const freshSummary = await fetchSummary();
            const { downloadQontactPointMonthlyAttendancePdf } = await import(
                "@components/QontactPoints/QontactPointMonthlyAttendancePdf"
            );
            downloadQontactPointMonthlyAttendancePdf({
                qontactPointName: qontactPoint.name,
                orgName: freshSummary.orgName,
                selectedMonth: month,
                selectedYear: year,
                records: freshSummary.records,
            });
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage("No se pudo generar la planilla mensual.".localized());
        } finally {
            setIsExporting(false);
        }
    };

    return {
        monthLabel,
        isCurrentMonth,
        previousMonth,
        nextMonth,
        summary,
        loading,
        isExporting,
        exportData,
        exportMonthlyAttendanceSheet,
    };
};
